import { Inject, Injectable } from '@angular/core';
import { FirebaseApp } from 'firebase/app';
import { FIREBASE_APP } from '../injections/firebase-app';
import { DataService } from './data.service';
import { BaseModel } from '@ag-common-lib/lib/models/base.model';
import {
  AgentMergeRequestModel,
  MergeStatus,
} from '@ag-common-lib/lib/models/functions/merge-tool/agent-merge-request.model';
import { FetchOptions, QueryParam, WhereFilterOperandKeys } from '../dao/CommonFireStoreDao.dao';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface MergeAgentLockModel extends BaseModel {
  agentDbId?: string;
  requestId?: string | null;
  ownerId?: string;
  expiresAt?: Date;
  updatedAt?: any;
}

const MERGE_REQUESTS_COLLECTION_NAME = 'merge-requests';

@Injectable({
  providedIn: 'root',
})
export class MergeAgentLocksService extends DataService<AgentMergeRequestModel> {
  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    super(fireBaseApp);
    this.collection = MERGE_REQUESTS_COLLECTION_NAME;
  }

  /**
   * Returns agent dbIds that are currently "locked" by a queued or in-progress merge request.
   */
  getLockedAgentDbIds(_now: Date = new Date(), fetchOptions?: FetchOptions): Observable<string[]> {
    const qp = [
      new QueryParam('execution.status', WhereFilterOperandKeys.in, [
        MergeStatus.initial,
        MergeStatus.inProgress,
        MergeStatus.queued,
      ]),
    ];

    // These are time-sensitive; avoid cached lists.
    return this.getList(qp, { ...(fetchOptions ?? {}), ignoreCash: true }).pipe(
      map((requests: AgentMergeRequestModel[] = []) => {
        const ids = new Set<string>();

        (Array.isArray(requests) ? requests : []).forEach(req => {
          const agentIds = [req?.targetAgentDbId, req?.sourceAgentDbId]
            .filter(v => typeof v === 'string' && !!v.trim())
            .map(v => (v as string).trim());

          agentIds.forEach(agentDbId => ids.add(agentDbId));
        });

        return Array.from(ids.values());
      }),
    );
  }

  getActiveInProgressRequests(fetchOptions?: FetchOptions): Observable<AgentMergeRequestModel[]> {
    const qp = [
      new QueryParam('execution.status', WhereFilterOperandKeys.in, [
        MergeStatus.initial,
        MergeStatus.inProgress,
        MergeStatus.queued,
      ]),
    ];
    return this.getList(qp, { ...(fetchOptions ?? {}), ignoreCash: true }).pipe(
      map(reqs => (Array.isArray(reqs) ? reqs : [])),
    );
  }
}
